import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Users, Eye, Phone, Mail, Calendar } from 'lucide-react';

const Patients = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchFilter, setSearchFilter] = useState('all');

  // Mock patient data - in real app would come from context/API
  const allPatients = [
    {
      id: '1',
      name: 'Mrs. Ngozi Okeke',
      mrn: 'MRN-20260130-AB123',
      email: 'ngozi.okeke@email.com',
      phone: '+234 803 456 7890',
      lastVisit: '2026-01-28',
      status: 'Active',
      patientType: 'Mother',
      condition: 'Prenatal Care'
    },
    {
      id: '2',
      name: 'Mrs. Fatima Bello',
      mrn: 'MRN-20260125-CD456',
      email: 'fatima.bello@email.com',
      phone: '+234 805 234 5678',
      lastVisit: '2026-01-27',
      status: 'Active',
      patientType: 'Mother',
      condition: 'Post Natal'
    },
    {
      id: '3',
      name: 'Baby David Okonkwo',
      mrn: 'MRN-20260120-EF789',
      email: 'grace.okonkwo@email.com',
      phone: '+234 806 789 0123',
      lastVisit: '2026-01-26',
      status: 'Active',
      patientType: 'Child',
      condition: 'Fever'
    },
    {
      id: '4',
      name: 'Mrs. Aisha Mohammed',
      mrn: 'MRN-20260115-GH012',
      email: 'aisha.mohammed@email.com',
      phone: '+234 807 890 1234',
      lastVisit: '2026-01-25',
      status: 'Active',
      patientType: 'Mother',
      condition: 'First Antenatal'
    },
    {
      id: '5',
      name: 'Baby Chioma Eze',
      mrn: 'MRN-20260110-IJ345',
      email: 'ada.eze@email.com',
      phone: '+234 808 901 2345',
      lastVisit: '2026-01-24',
      status: 'Active',
      patientType: 'Child',
      condition: 'Nutrition'
    }
  ];

  // Filter patients based on search term and filter
  const filteredPatients = allPatients.filter(patient => {
    const searchLower = searchTerm.toLowerCase();
    
    // Check if search term matches name, MRN, or email
    const matchesSearch = 
      patient.name.toLowerCase().includes(searchLower) ||
      patient.mrn.toLowerCase().includes(searchLower) ||
      patient.email.toLowerCase().includes(searchLower);

    // Apply filter
    if (searchFilter === 'all') return matchesSearch;
    if (searchFilter === 'mother') return matchesSearch && patient.patientType === 'Mother';
    if (searchFilter === 'child') return matchesSearch && patient.patientType === 'Child';
    
    return matchesSearch;
  });

  return (
    <div className="p-4 md:p-6 lg:p-8 min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex items-center gap-3 md:gap-4 mb-2">
            <div className="p-2 md:p-3 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl md:rounded-2xl shadow-lg shadow-emerald-500/30">
              <Users className="w-6 h-6 md:w-8 md:h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900">
                Patient Management
              </h1>
              <p className="text-sm md:text-base text-gray-600 mt-1">Search and manage patient records</p>
            </div>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-xl md:rounded-2xl shadow-lg border border-gray-100 p-4 md:p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search Input */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, MRN, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
              />
            </div>

            {/* Filter Buttons */}
            <div className="flex gap-2">
              <button
                onClick={() => setSearchFilter('all')}
                className={`px-4 py-3 rounded-xl font-medium transition-all ${
                  searchFilter === 'all'
                    ? 'bg-emerald-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setSearchFilter('mother')}
                className={`px-4 py-3 rounded-xl font-medium transition-all ${
                  searchFilter === 'mother'
                    ? 'bg-emerald-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Mothers
              </button>
              <button
                onClick={() => setSearchFilter('child')}
                className={`px-4 py-3 rounded-xl font-medium transition-all ${
                  searchFilter === 'child'
                    ? 'bg-emerald-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Children
              </button>
            </div>
          </div>

          {/* Results Count */}
          <div className="mt-4 text-sm text-gray-600">
            Showing {filteredPatients.length} of {allPatients.length} patients
          </div>
        </div>

        {/* Patients List */}
        <div className="space-y-4">
          {filteredPatients.length > 0 ? (
            filteredPatients.map(patient => (
              <div
                key={patient.id}
                className="bg-white rounded-xl shadow-lg border border-gray-100 p-4 md:p-6 hover:shadow-xl transition-shadow"
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  {/* Patient Info */}
                  <div className="flex-1">
                    <div className="flex items-start gap-4">
                      {/* Avatar */}
                      <div className="w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-emerald-500 to-green-600 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                        {patient.name.charAt(0)}
                      </div>

                      {/* Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-3 mb-2">
                          <h3 className="text-lg md:text-xl font-bold text-gray-900">{patient.name}</h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                            patient.patientType === 'Mother' 
                              ? 'bg-pink-100 text-pink-700' 
                              : 'bg-emerald-100 text-emerald-700'
                          }`}>
                            {patient.patientType}
                          </span>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <span className="font-mono font-semibold text-emerald-600">{patient.mrn}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <span className="truncate">{patient.email}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <span>{patient.phone}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">Last visit:</span>
                            <span className="font-medium">{new Date(patient.lastVisit).toLocaleDateString()}</span>
                          </div>
                        </div>

                        <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-emerald-50 text-emerald-700 rounded-lg text-sm font-medium">
                          <span className="w-2 h-2 bg-emerald-600 rounded-full"></span>
                          {patient.condition}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 lg:flex-col">
                    <Link
                      to={`/nurse/patient-record/${patient.id}`}
                      className="flex-1 lg:flex-none flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl hover:shadow-lg hover:scale-105 transition-all font-medium text-sm shadow-md shadow-emerald-500/30"
                    >
                      <Eye className="w-4 h-4" />
                      <span>View Record</span>
                    </Link>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 md:p-12 text-center">
              <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">No patients found</h3>
              <p className="text-gray-600">
                {searchTerm 
                  ? 'Try adjusting your search criteria'
                  : 'Add your first patient to get started'}
              </p>
              {!searchTerm && (
                <Link
                  to="/nurse/add-patient"
                  className="inline-block mt-4 px-6 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl hover:shadow-lg hover:scale-105 transition-all font-semibold shadow-md shadow-emerald-500/30"
                >
                  Add Patient
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Patients;
